import React, { useCallback } from 'react'

import { HiLockClosed, HiLockOpen } from 'react-icons/hi2'

import Switch from '@/components/common/Switch'

interface VisibilityToggleProps {
  isUnrestricted: boolean
  onChange: (isUnrestricted: boolean) => void
  resourceType?: string
  parentResourceType?: string
  isNew?: boolean
  isPlural?: boolean
  unrestrictedDescription?: string
  restrictedDescription?: string
}

export const VisibilityToggle: React.FC<VisibilityToggleProps> = ({
  isUnrestricted,
  onChange,
  resourceType = 'resource',
  parentResourceType = 'resource',
  isNew = false,
  isPlural = false,
  unrestrictedDescription,
  restrictedDescription,
}) => {
  const normalizedResourceType = resourceType.toLowerCase()
  const normalizedParentResourceType = parentResourceType.toLowerCase()
  const target = isPlural
    ? `these ${normalizedResourceType}s`
    : `this ${normalizedResourceType}`

  const handleChange = useCallback(() => {
    onChange(!isUnrestricted)
  }, [onChange, isUnrestricted])

  const description = isUnrestricted
    ? (unrestrictedDescription ??
      (isNew
        ? `Everyone who has access to the parent ${normalizedParentResourceType} will be able to access ${target}`
        : `Everyone who has access to the parent ${normalizedParentResourceType} can access ${target}`))
    : (restrictedDescription ??
      (isNew
        ? `Only members who are explicitly granted access and admins will be able to access ${target}`
        : `Only members who are explicitly granted access and admins can access ${target}`))

  return (
    <div className="flex items-center justify-between gap-4">
      <div className="flex items-center gap-3 min-w-0">
        {isUnrestricted ? (
          <HiLockOpen className="w-5 h-5 text-green-500" />
        ) : (
          <HiLockClosed className="w-5 h-5 text-amber-500" />
        )}
        <div className="flex flex-col min-w-0">
          <span className="text-sm font-medium text-primary-900 dark:text-primary-100">
            {isUnrestricted ? 'Open to team' : 'Invite only'}
          </span>
          <span className="text-xs text-primary-500 dark:text-primary-400">
            {description}
          </span>
        </div>
      </div>
      <Switch
        checked={isUnrestricted}
        onClick={handleChange}
        className="cursor-pointer transition-colors duration-200 ease-in-out flex-shrink-0"
        thumbClassName="duration-200 ease-in-out"
      />
    </div>
  )
}

export default VisibilityToggle
